import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  collection,
  query,
  where,
  getDocs,
  serverTimestamp
} from 'firebase/firestore';
import { db } from './firebase';
import { createBadgeNotification } from './notificationService';

// Badge Categories
export const BADGE_CATEGORIES = {
  ENGAGEMENT: 'engagement',
  SOCIAL_REACTION: 'social_reaction',
  STREAK_CONSISTENCY: 'streak_consistency',
  SOCIAL_MILESTONE: 'social_milestone',
  META_SYSTEM: 'meta_system'
};

// Comprehensive Badge Definitions
export const ALL_BADGES = {
  // 🧠 Engagement Badges
  first_take: {
    id: 'first_take',
    name: 'First Take',
    description: 'Posted your first debate',
    icon: '🗣️',
    category: BADGE_CATEGORIES.ENGAGEMENT,
    rarity: 'common',
    condition: (stats, userProgress) => userProgress.debatesPosted >= 1
  },
  hot_take_approved: {
    id: 'hot_take_approved',
    name: 'Hot Take Approved',
    description: 'Got approved to the Hot Takes feed',
    icon: '🔥✅',
    category: BADGE_CATEGORIES.ENGAGEMENT,
    rarity: 'uncommon',
    condition: (stats, userProgress) => userProgress.hotTakesCreated >= 1
  },
  ten_debates: {
    id: 'ten_debates',
    name: '10 Debates',
    description: 'Posted 10 debates total',
    icon: '📣',
    category: BADGE_CATEGORIES.ENGAGEMENT,
    rarity: 'uncommon',
    condition: (stats, userProgress) => userProgress.debatesPosted >= 10
  },
  debate_machine: {
    id: 'debate_machine',
    name: 'Debate Machine',
    description: '50 debates posted - you\'re unstoppable!',
    icon: '🎤',
    category: BADGE_CATEGORIES.ENGAGEMENT,
    rarity: 'rare',
    condition: (stats, userProgress) => userProgress.debatesPosted >= 50
  },
  first_vote: {
    id: 'first_vote',
    name: 'First Opinion',
    description: 'Cast your first vote',
    icon: '🗳️',
    category: BADGE_CATEGORIES.ENGAGEMENT,
    rarity: 'common',
    condition: (stats, userProgress) => stats.debates >= 1
  },
  first_comment: {
    id: 'first_comment',
    name: 'Voice Heard',
    description: 'Made your first comment',
    icon: '💬',
    category: BADGE_CATEGORIES.ENGAGEMENT,
    rarity: 'common',
    condition: (stats, userProgress) => userProgress.commentsPosted >= 1
  },

  // 💬 Social Reaction Badges
  clapback_rookie: {
    id: 'clapback_rookie',
    name: 'Clapback Rookie',
    description: 'Received your first 10 claps',
    icon: '👏',
    category: BADGE_CATEGORIES.SOCIAL_REACTION,
    rarity: 'common',
    condition: (stats, userProgress) => stats.clapsReceived >= 10
  },
  spicy_opinion: {
    id: 'spicy_opinion',
    name: 'Spicy Opinion',
    description: 'Got 5 tomatoes on a single take',
    icon: '🍅💥',
    category: BADGE_CATEGORIES.SOCIAL_REACTION,
    rarity: 'uncommon',
    condition: (stats, userProgress) => userProgress.maxTomatoesOnSingleTake >= 5
  },
  controversial_genius: {
    id: 'controversial_genius',
    name: 'Controversial Genius',
    description: 'Got 50+ claps AND 20+ tomatoes on one take',
    icon: '🤯',
    category: BADGE_CATEGORIES.SOCIAL_REACTION,
    rarity: 'legendary',
    condition: (stats, userProgress) => 
      userProgress.maxClapsOnSingleTake >= 50 && userProgress.maxTomatoesOnSingleTake >= 20
  },
  clap_master: {
    id: 'clap_master',
    name: 'Clap Master',
    description: 'Received 100+ total claps',
    icon: '👏💯',
    category: BADGE_CATEGORIES.SOCIAL_REACTION,
    rarity: 'rare',
    condition: (stats, userProgress) => stats.clapsReceived >= 100
  },
  tomato_target: {
    id: 'tomato_target',
    name: 'Tomato Target',
    description: 'Received 50+ total tomatoes',
    icon: '🍅🎯',
    category: BADGE_CATEGORIES.SOCIAL_REACTION,
    rarity: 'uncommon',
    condition: (stats, userProgress) => stats.tomatoesReceived >= 50
  },
  reaction_magnet: {
    id: 'reaction_magnet',
    name: 'Reaction Magnet',
    description: 'Got 200+ total reactions (claps + tomatoes)',
    icon: '🧲⚡',
    category: BADGE_CATEGORIES.SOCIAL_REACTION,
    rarity: 'rare',
    condition: (stats, userProgress) => (stats.clapsReceived + stats.tomatoesReceived) >= 200
  },

  // 📈 Streak & Consistency Badges (from existing streak system)
  ember_streak: {
    id: 'ember_streak',
    name: '3-Day Streak',
    description: 'Maintained activity for 3 consecutive days',
    icon: '🎯',
    category: BADGE_CATEGORIES.STREAK_CONSISTENCY,
    rarity: 'common',
    condition: (stats, userProgress) => stats.streak >= 3
  },
  flame_streak: {
    id: 'flame_streak',
    name: 'Flame Streak',
    description: 'One week of consistent engagement',
    icon: '🔥',
    category: BADGE_CATEGORIES.STREAK_CONSISTENCY,
    rarity: 'uncommon',
    condition: (stats, userProgress) => stats.streak >= 7
  },
  inferno_streak: {
    id: 'inferno_streak',
    name: '14-Day Inferno',
    description: 'Two weeks of dedication',
    icon: '🔥💪',
    category: BADGE_CATEGORIES.STREAK_CONSISTENCY,
    rarity: 'rare',
    condition: (stats, userProgress) => stats.streak >= 14
  },
  eternal_debate: {
    id: 'eternal_debate',
    name: 'Eternal Debater',
    description: 'Legendary 30-day streak master',
    icon: '🏆',
    category: BADGE_CATEGORIES.STREAK_CONSISTENCY,
    rarity: 'legendary',
    condition: (stats, userProgress) => stats.streak >= 30
  },

  // 👥 Social Milestone Badges
  first_follower: {
    id: 'first_follower',
    name: 'First Follower',
    description: 'Someone believes in your takes!',
    icon: '🧠⭐',
    category: BADGE_CATEGORIES.SOCIAL_MILESTONE,
    rarity: 'common',
    condition: (stats, userProgress) => stats.followers >= 1
  },
  debate_leader: {
    id: 'debate_leader',
    name: 'Debate Leader',
    description: 'Reached 100 followers',
    icon: '📈👑',
    category: BADGE_CATEGORIES.SOCIAL_MILESTONE,
    rarity: 'rare',
    condition: (stats, userProgress) => stats.followers >= 100
  },
  influencer: {
    id: 'influencer',
    name: 'Influencer',
    description: '50+ followers believe in your takes',
    icon: '⭐💫',
    category: BADGE_CATEGORIES.SOCIAL_MILESTONE,
    rarity: 'uncommon',
    condition: (stats, userProgress) => stats.followers >= 50
  },
  social_butterfly: {
    id: 'social_butterfly',
    name: 'Social Butterfly',
    description: 'Following 25+ other debaters',
    icon: '🦋💬',
    category: BADGE_CATEGORIES.SOCIAL_MILESTONE,
    rarity: 'common',
    condition: (stats, userProgress) => stats.following >= 25
  },

  // 🧪 Meta / System-Driven Badges
  beta_tester: {
    id: 'beta_tester',
    name: 'Beta Tester',
    description: 'Early access pioneer',
    icon: '🧪⚗️',
    category: BADGE_CATEGORIES.META_SYSTEM,
    rarity: 'special',
    condition: (stats, userProgress) => userProgress.joinDate && userProgress.joinDate < new Date('2025-07-01')
  },
  launch_og: {
    id: 'launch_og',
    name: 'Launch OG',
    description: 'Active during launch week',
    icon: '🎉🚀',
    category: BADGE_CATEGORIES.META_SYSTEM,
    rarity: 'special',
    condition: (stats, userProgress) => 
      userProgress.joinDate && 
      userProgress.joinDate >= new Date('2025-06-01') && 
      userProgress.joinDate <= new Date('2025-06-07')
  },
  bug_squasher: {
    id: 'bug_squasher',
    name: 'Bug Squasher',
    description: 'Reported a verified issue',
    icon: '🐛🔧',
    category: BADGE_CATEGORIES.META_SYSTEM,
    rarity: 'special',
    condition: (stats, userProgress) => userProgress.bugsReported >= 1
  },
  early_bird: {
    id: 'early_bird',
    name: 'Early Bird',
    description: 'One of the first 100 users',
    icon: '🌅🏃',
    category: BADGE_CATEGORIES.META_SYSTEM,
    rarity: 'special',
    condition: (stats, userProgress) => userProgress.userNumber <= 100
  },
  night_owl: {
    id: 'night_owl',
    name: 'Night Owl',
    description: 'Active after midnight 10+ times',
    icon: '🦉🌙',
    category: BADGE_CATEGORIES.META_SYSTEM,
    rarity: 'uncommon',
    condition: (stats, userProgress) => userProgress.lateNightSessions >= 10
  }
};

// Badge rarity levels
export const BADGE_RARITIES = {
  common: { color: '#9CA3AF', glow: false },
  uncommon: { color: '#10B981', glow: true },
  rare: { color: '#3B82F6', glow: true },
  epic: { color: '#8B5CF6', glow: true },
  legendary: { color: '#F59E0B', glow: true },
  special: { color: '#EF4444', glow: true }
};

// Get user's badge progress data
export const getUserBadgeProgress = async (userId) => {
  try {
    const progressRef = doc(db, 'users', userId, 'progress', 'badges');
    const progressDoc = await getDoc(progressRef);
    
    if (progressDoc.exists()) {
      return progressDoc.data();
    }
    
    // Default progress data
    return {
      debatesPosted: 0,
      hotTakesCreated: 0,
      commentsPosted: 0,
      maxClapsOnSingleTake: 0,
      maxTomatoesOnSingleTake: 0,
      bugsReported: 0,
      lateNightSessions: 0,
      joinDate: new Date(),
      userNumber: 999
    };
  } catch (error) {
    console.error('Error getting badge progress:', error);
    return {};
  }
};

// Update user's badge progress
export const updateBadgeProgress = async (userId, progressUpdate) => {
  try {
    const progressRef = doc(db, 'users', userId, 'progress', 'badges');
    await setDoc(progressRef, progressUpdate, { merge: true });
  } catch (error) {
    console.error('Error updating badge progress:', error);
  }
};

// Get user's earned badges
export const getUserBadges = async (userId) => {
  try {
    const badgesRef = collection(db, 'users', userId, 'badges');
    const badgesSnapshot = await getDocs(badgesRef);
    
    return badgesSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      earnedAt: doc.data().earnedAt?.toDate() || new Date()
    }));
  } catch (error) {
    console.error('Error getting user badges:', error);
    return [];
  }
};

// Award a badge to user
export const awardBadge = async (userId, badgeId) => {
  try {
    const badgeData = ALL_BADGES[badgeId];
    if (!badgeData) {
      throw new Error(`Badge ${badgeId} not found`);
    }

    const badgeRef = doc(db, 'users', userId, 'badges', badgeId);
    const badgeDoc = await getDoc(badgeRef);
    
    // Don't award if already earned
    if (badgeDoc.exists()) {
      return false;
    }

    // Award the badge
    await setDoc(badgeRef, {
      ...badgeData,
      earnedAt: serverTimestamp(),
      awardedAt: Date.now()
    });

    // Send notification
    await createBadgeNotification(userId, badgeData.name, badgeData.icon);

    return true;
  } catch (error) {
    console.error('Error awarding badge:', error);
    return false;
  }
};

// Check for newly earned badges
export const checkForNewBadges = async (userId, stats, userProgress) => {
  try {
    const earnedBadges = await getUserBadges(userId);
    const earnedBadgeIds = new Set(earnedBadges.map(b => b.id));
    const newlyEarnedBadges = [];

    for (const [badgeId, badge] of Object.entries(ALL_BADGES)) {
      if (!earnedBadgeIds.has(badgeId) && badge.condition(stats, userProgress)) {
        const wasAwarded = await awardBadge(userId, badgeId);
        if (wasAwarded) {
          newlyEarnedBadges.push(badge);
        }
      }
    }

    return newlyEarnedBadges;
  } catch (error) {
    console.error('Error checking for new badges:', error);
    return [];
  }
};

// Get badges by category
export const getBadgesByCategory = (earnedBadges) => {
  const categorized = {};
  
  for (const category of Object.values(BADGE_CATEGORIES)) {
    categorized[category] = [];
  }

  earnedBadges.forEach(badge => {
    const badgeData = ALL_BADGES[badge.id];
    if (badgeData && categorized[badgeData.category]) {
      categorized[badgeData.category].push({
        ...badge,
        ...badgeData
      });
    }
  });

  return categorized;
};

// Get badge completion stats
export const getBadgeStats = async (userId) => {
  try {
    const earnedBadges = await getUserBadges(userId);
    const totalBadges = Object.keys(ALL_BADGES).length;
    const earnedCount = earnedBadges.length;

    const categoryStats = {};
    for (const category of Object.values(BADGE_CATEGORIES)) {
      const categoryBadges = Object.values(ALL_BADGES).filter(b => b.category === category);
      const earnedInCategory = earnedBadges.filter(eb => {
        const badgeData = ALL_BADGES[eb.id];
        return badgeData && badgeData.category === category;
      });
      
      categoryStats[category] = {
        total: categoryBadges.length,
        earned: earnedInCategory.length,
        percentage: Math.round((earnedInCategory.length / categoryBadges.length) * 100)
      };
    }

    return {
      totalBadges,
      earnedCount,
      completionPercentage: Math.round((earnedCount / totalBadges) * 100),
      categoryStats,
      earnedBadges
    };
  } catch (error) {
    console.error('Error getting badge stats:', error);
    return null;
  }
};

// Track specific activities for badge progress
export const trackBadgeActivity = async (userId, activityType, activityData = {}) => {
  try {
    const currentProgress = await getUserBadgeProgress(userId);
    let progressUpdate = {};

    switch (activityType) {
      case 'debate_posted':
        progressUpdate.debatesPosted = (currentProgress.debatesPosted || 0) + 1;
        break;
      
      case 'hot_take_created':
        progressUpdate.hotTakesCreated = (currentProgress.hotTakesCreated || 0) + 1;
        break;
      
      case 'comment_posted':
        progressUpdate.commentsPosted = (currentProgress.commentsPosted || 0) + 1;
        break;
      
      case 'claps_received':
        const newClaps = activityData.count || 1;
        progressUpdate.maxClapsOnSingleTake = Math.max(
          currentProgress.maxClapsOnSingleTake || 0, 
          newClaps
        );
        break;
      
      case 'tomatoes_received':
        const newTomatoes = activityData.count || 1;
        progressUpdate.maxTomatoesOnSingleTake = Math.max(
          currentProgress.maxTomatoesOnSingleTake || 0, 
          newTomatoes
        );
        break;
      
      case 'bug_reported':
        progressUpdate.bugsReported = (currentProgress.bugsReported || 0) + 1;
        break;
      
      case 'late_night_session':
        progressUpdate.lateNightSessions = (currentProgress.lateNightSessions || 0) + 1;
        break;
    }

    if (Object.keys(progressUpdate).length > 0) {
      await updateBadgeProgress(userId, progressUpdate);
      
      // Check for new badges with updated progress
      const updatedProgress = { ...currentProgress, ...progressUpdate };
      // We'll need stats from the calling function
      return updatedProgress;
    }

    return currentProgress;
  } catch (error) {
    console.error('Error tracking badge activity:', error);
    return {};
  }
};